import ContentBox from 'components/ContentBox'
import Page from 'components/Page'
import SectionItems from 'components/Section/Section.Items'
import TextTitle from 'components/TextTitle'
import { View } from 'components/View'
import SearchInput from 'pages/Search/Input'
import SectionItem from 'containers/SectionItem'
import { ARTICLE_MODEL_CLASS_NAME } from 'models/daos/article/constants'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import {
  SEARCH_SECTION_ITEMS_COUNT,
  SECTION_TO_ITEMS
} from 'modules/search/constants'
import { getSearchedItemsSectionSelector } from 'modules/section/selectors/get-searched-items'
import { SECTIONS_NAME } from 'modules/sections/constants/index.cjs'
import React from 'react'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import getInstanceSectionSelector from 'modules/section/selectors/get-instance'
import { loadCollectionRouteAction } from 'modules/routes/actions'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import {
  debounceUtil,
  logUtil,
  omitObjectUtil
} from 'packages/src'
import {
  getFoundExercisesSelector,
  loadExercisesAction,
  updateQueryExercisesUiAction
} from '@gymaholic/packages'
import { defineMessages } from 'react-intl'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import useIntl from 'modules/component/utils/hooks/useIntl'
import ExerciseCard from 'components/ExerciseCard'
import getQuerySearchSelect from 'modules/search/selectors/get-query'
import loadRecommendedPlansAction from 'modules/plan/redux/actions/load-recommended'
import getRecommendedDomainListDataPlanSelect from 'modules/plan/redux/selectors/get-recommended-domain-list-data'
import { PlanCard } from 'containers/Plan/Card'
import getRecommendedDomainListDataWorkoutSelect from 'modules/workout/redux/selectors/get-recommended-domain-list-data'
import WorkoutCard from 'containers/Workout/Card'
import loadRecommendedWorkoutsAction from 'modules/workout/redux/actions/load-recommended'
import getPathnameRouterSelect from 'modules/router/selectors/get-pathname'
import getActionStrReduxUtil from 'modules/redux/utils/get-action-str/index.js'
import { SearchPageSuggestions } from 'pages/Search/SearchSuggestions/index.js'
import { isSearchLoadedSectionSelector } from 'modules/section/selectors/isSearchLoaded/index.js'
import { PageLoader } from 'components/Page/Page.Loader.js'

/**
 * Gets the section key from the pathname.
 *
 * @param {string} pathname - The current pathname.
 * @returns {string|null} - The matching section name, or null if not found.
 */
export function getSectionFromPathnameUtil (pathname) {
  if (!pathname || pathname === '/search') {
    return null
  }

  const lowerPath = pathname.toLowerCase()

  // Match any known section by slug
  // eslint-disable-next-line no-unused-vars
  for (const [_, section] of Object.entries(SECTION_TO_ITEMS)) {
    if (lowerPath.includes(`/${section}`)) {
      return section
    }
  }

  return null
}

const ANALYTIC_EVENT_PREFIX = 'SEARCH'

const messages = defineMessages({
  notFound: {
    id: 'SearchPage.notFound',
    defaultMessage: 'We could not find any content matching this query.'
  },
  title: {
    id: 'SearchPage.title',
    defaultMessage: 'Gymaholic Search'
  },
  titleSearch: {
    id: 'SearchPage.titleSearch',
    defaultMessage: 'Gymaholic Search: {query}'
  },
  articlePlaceholder: {
    id: 'SearchPage.articlePlaceholder',
    defaultMessage: 'e.g. how to lose weight fast, how to build muscle, etc.'
  },
  motivationPlaceholder: {
    id: 'SearchPage.motivationPlaceholder',
    defaultMessage: 'e.g. consistency, inspiration, etc.'
  },
  planPlaceholder: {
    id: 'SearchPage.planPlaceholder',
    defaultMessage: 'e.g. calisthenics plan, weight loss plan, etc.'
  },
  workoutsPlaceholder: {
    id: 'SearchPage.workoutsPlaceholder',
    defaultMessage: 'e.g. calisthenics workout, HIIT workout, etc.'
  },
  exercisesPlaceholder: {
    id: 'SearchPage.exercisesPlaceholder',
    defaultMessage: 'e.g. squat, deadlift, etc.'
  }
})

const SECTION_TO_PLACEHOLDER = {
  // [SECTION_TO_ITEMS.Articles]: messages.articlePlaceholder,
  // [SECTION_TO_ITEMS.Motivation]: messages.motivationPlaceholder,
  // [SECTION_TO_ITEMS.Plans]: messages.planPlaceholder,
  // [SECTION_TO_ITEMS.Workouts]: messages.workoutsPlaceholder,
  // [SECTION_TO_ITEMS.Exercises]: messages.exercisesPlaceholder
}

const DEBOUNCE_TIMER_IN_MS = 300
export default function SearchPage (props) {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { location } = props
  const instance = useSelector(state => getInstanceSectionSelector()(state, props))
  const articles = useSelector(state => getSearchedItemsSectionSelector(SECTIONS_NAME.ARTICLES)(state, props))
  const motivation = useSelector(state => getSearchedItemsSectionSelector(SECTIONS_NAME.MOTIVATION)(state, props))
  const pathname = useSelector(getPathnameRouterSelect)
  const hasCategory = pathname !== '/search'
  const isExerciseCategory = pathname === '/search/exercises'
  const isPlanCategory = pathname === '/search/plans'
  const isWorkoutCategory = pathname === '/search/workouts'
  const exerciseIds = useSelector(getFoundExercisesSelector())
  const query = useSelector(getQuerySearchSelect)
  const planIds = useSelector(getRecommendedDomainListDataPlanSelect) || []
  const workoutIds = useSelector(getRecommendedDomainListDataWorkoutSelect) || []
  const section = getSectionFromPathnameUtil(pathname)
  const isSearchLoaded = useSelector(isSearchLoadedSectionSelector(section || SECTIONS_NAME.ARTICLES))

  let sectionKeyToItems = {
    [SECTION_TO_ITEMS.Articles]: articles,
    [SECTION_TO_ITEMS.Motivation]: motivation,
    [SECTION_TO_ITEMS.Plans]: planIds,
    [SECTION_TO_ITEMS.Workouts]: workoutIds,
    [SECTION_TO_ITEMS.Exercises]: exerciseIds
  }
  if (hasCategory && !isExerciseCategory) {
    sectionKeyToItems = omitObjectUtil(sectionKeyToItems, [SECTION_TO_ITEMS.Exercises])
  }
  if (hasCategory && !isWorkoutCategory) {
    sectionKeyToItems = omitObjectUtil(sectionKeyToItems, [SECTION_TO_ITEMS.Workouts])
  }
  if (hasCategory && !isPlanCategory) {
    sectionKeyToItems = omitObjectUtil(sectionKeyToItems, [SECTION_TO_ITEMS.Plans])
  }

  const debugProps = {
    articles,
    motivation,
    instance,
    exerciseIds,
    planIds,
    workoutIds,
    hasCategory,
    isExerciseCategory,
    sectionKeyToItems,
    section
  }
  logUtil('SearchPage').debug('debugProps=', debugProps)

  const loadData = (query) => {
    dispatch(
      loadCollectionRouteAction({
        route: props.route,
        match: props.match,
        options: {
          location: props.location
        }
      })
    )

    if (!section || section === SECTION_TO_ITEMS.Exercises) {
      dispatch(loadExercisesAction(query))
    }

    if (!section || section === SECTION_TO_ITEMS.Plans) {
      dispatch(
        loadRecommendedPlansAction({
          ignoreTag: true
        })
      )
    }

    if (!section || section === SECTION_TO_ITEMS.Workouts) {
      dispatch(
        loadRecommendedWorkoutsAction({
          ignoreTag: true
        })
      )
    }
  }
  const loadDataDebouncedRef = React.useRef(
    debounceUtil(loadData, DEBOUNCE_TIMER_IN_MS)
  )

  const handleSubmit = (query) => {
    const shouldUpdateExerciseQuery = !section || section === SECTION_TO_ITEMS.Exercises
    if (shouldUpdateExerciseQuery) {
      dispatch(updateQueryExercisesUiAction(query))
    }
    if (query) {
      loadDataDebouncedRef.current(query)
    }
  }

  const renderSections = () => {
    if (!query) {
      return
    }

    if (!isSearchLoaded) {
      return <PageLoader />
    }

    const isQueryNotFound = Object
      .keys(SECTION_TO_ITEMS)
      .every((key) => !sectionKeyToItems[SECTION_TO_ITEMS[key]]?.length)

    if (isQueryNotFound) {
      return (
        <ContentBox mx>
          <span>{formatMessageIntlUtil(intl, messages.notFound)}</span>
        </ContentBox>
      )
    }

    return (
      <ContentBox
        mt={0}
        p={0}
      >
        {
          Object.keys(SECTION_TO_ITEMS).map((key, sectionIndex) => {
            const value = SECTION_TO_ITEMS[key]
            if (!sectionKeyToItems[value]?.length) {
              return <></>
            }

            const itemSection = key.toLowerCase()
            if (section && section !== itemSection) {
              return <></>
            }

            const searchLimit = SEARCH_SECTION_ITEMS_COUNT[itemSection]
            const items = sectionKeyToItems[value].slice(0, searchLimit)
            const isExercise = value === SECTION_TO_ITEMS.Exercises
            const isPlan = value === SECTION_TO_ITEMS.Plans
            const isWorkout = value === SECTION_TO_ITEMS.Workouts

            const renderItem = (item, index) => {
              const itemKey = `${key}-${item}-${index}`
              if (isWorkout) {
                return (
                  <WorkoutCard
                    id={item}
                    key={itemKey}
                    isClickable
                    shouldShowViewButton={false}
                    isFixedWidth
                  />
                )
              }

              if (isPlan) {
                return (
                  <PlanCard
                    id={item}
                    key={itemKey}
                    isClickable
                    shouldShowViewButton={false}
                    isFixedWidth
                  />
                )
              }

              if (isExercise) {
                return (
                  <ExerciseCard
                    id={item}
                    key={itemKey}
                  />
                )
              }

              return (
                <SectionItem
                  key={`${key}-${getIdReduxUtil(item)}-${index}`}
                  hasFixedWidth
                  instance={item}
                  isHorizontal={item.className === ARTICLE_MODEL_CLASS_NAME}
                  analyticEventPrefix={getActionStrReduxUtil(ANALYTIC_EVENT_PREFIX, item.className)}
                />
              )
            }

            return (
              <View key={`${key}-${sectionIndex}`}>
                <TextTitle px={3}>
                  {key}
                </TextTitle>
                <SectionItems
                  direction={SectionItems.DIRECTION.ROW}
                  pathname={location.pathname}
                  shouldScroll
                >
                  {
                    items.map(renderItem)
                  }
                </SectionItems>
              </View>
            )
          })
        }
      </ContentBox>
    )
  }

  const message = query ? messages.titleSearch : messages.title
  const title = formatMessageIntlUtil(intl, message, { query })
  const placeholderMessage = SECTION_TO_PLACEHOLDER[section]
  const placeholder = formatMessageIntlUtil(intl, placeholderMessage)
  return (
    <Page
      value={instance}
      title={title}
      shouldNotHavePadding
    >
      <ContentBox px={3}>
        <SearchInput
          onSubmit={handleSubmit}
          isFocused
          placeholder={placeholder}
        />

        <SearchPageSuggestions
          section={section}
          onSubmit={handleSubmit}
        />
      </ContentBox>
      {renderSections()}
    </Page>
  )
}
